import { isBefore, isSameDay, startOfToday } from "date-fns";
import { TFunction } from "i18next";
import * as Yup from "yup";

import { CalculationParameterKey, CalculationParameterResponse, CalculationParameterUnit } from "@/api";

import {
  DecimalScale,
  ParameterEntryFormValues,
  ParameterFormValues,
  PercentageMax,
} from "./CalculationParametersSettings.types";
import { crossCheckDates, isEffectiveFromEditable, savedValueOn } from "./CalculationParametersSettings.utils";

const DecimalFactor = 10 ** DecimalScale;

interface CrossParameterRule {
  otherKey: CalculationParameterKey;
  isViolated: (value: number, other: number) => boolean;
  message: string;
}

const isBelow = (value: number, other: number) => value < other;
const isAbove = (value: number, other: number) => value > other;

/**
 * Validation for the values of one calculation parameter. Mirrors the server rules so the Supervisor gets
 * instant feedback; the server remains the source of truth (e.g. for concurrent edits).
 */
export function calculationParameterValidationSchema(
  t: TFunction,
  selected: CalculationParameterResponse | null,
  parameters: CalculationParameterResponse[]
) {
  const valueLabel = t("settings.parameters.value");
  const effectiveFromLabel = t("settings.parameters.effectiveFrom");
  const isPercentage = selected?.unit === CalculationParameterUnit.Percentage;

  let valueSchema = Yup.number()
    .required(t("common.validation.fieldRequired", { fieldName: valueLabel }))
    .min(0, t("settings.parameters.validation.notNegative"))
    .test(
      "decimals",
      t("settings.parameters.validation.maxDecimals"),
      (value) => Math.round(value * DecimalFactor) / DecimalFactor === value
    );

  if (isPercentage) {
    valueSchema = valueSchema.max(PercentageMax, t("settings.parameters.validation.percentageRange"));
  }

  const hoechstBelowSockelEk = t("settings.parameters.validation.hoechstBelowSockelEK");
  const hoechstBelowSockelAms = t("settings.parameters.validation.hoechstBelowSockelAMS");
  const crossParameterRules: Partial<Record<CalculationParameterKey, CrossParameterRule[]>> = {
    [CalculationParameterKey.HoechstbetragEk]: [
      { otherKey: CalculationParameterKey.SockelbetragEk, isViolated: isBelow, message: hoechstBelowSockelEk },
      { otherKey: CalculationParameterKey.SockelbetragAms, isViolated: isBelow, message: hoechstBelowSockelAms },
    ],
    [CalculationParameterKey.SockelbetragEk]: [
      { otherKey: CalculationParameterKey.HoechstbetragEk, isViolated: isAbove, message: hoechstBelowSockelEk },
    ],
    [CalculationParameterKey.SockelbetragAms]: [
      { otherKey: CalculationParameterKey.HoechstbetragEk, isViolated: isAbove, message: hoechstBelowSockelAms },
    ],
  };
  const rules = selected == null ? [] : (crossParameterRules[selected.key] ?? []);

  valueSchema = valueSchema.test("crossParameter", "", function crossParameterTest(value) {
    const effectiveFrom = (this.parent as ParameterEntryFormValues).effectiveFrom;
    const root = this.from?.[this.from.length - 1]?.value as ParameterFormValues | undefined;
    if (rules.length === 0 || effectiveFrom == null || root == null) {
      return true;
    }

    const otherKeys = rules.map((rule) => rule.otherKey);
    for (const date of crossCheckDates(effectiveFrom, root.entries, parameters, otherKeys)) {
      for (const rule of rules) {
        const other = savedValueOn(parameters, rule.otherKey, date);
        if (other != null && rule.isViolated(value, other)) {
          return this.createError({ message: rule.message });
        }
      }
    }

    return true;
  });

  const effectiveFromSchema = Yup.date()
    .nullable()
    .required(t("common.validation.fieldRequired", { fieldName: effectiveFromLabel }))
    .test("notInPast", t("settings.parameters.validation.effectiveFromInPast"), function notInPastTest(value) {
      const entry = this.parent as ParameterEntryFormValues;
      return !isEffectiveFromEditable(entry) || !isBefore(value, startOfToday());
    })
    .test("unique", t("settings.parameters.validation.duplicateEffectiveFrom"), function uniqueTest(value) {
      const root = this.from?.[this.from.length - 1]?.value as ParameterFormValues | undefined;
      if (root == null) {
        return true;
      }

      return root.entries.filter((e) => e.effectiveFrom != null && isSameDay(e.effectiveFrom, value)).length <= 1;
    });

  return Yup.object().shape({
    entries: Yup.array().of(
      Yup.object().shape({
        effectiveFrom: effectiveFromSchema,
        value: valueSchema,
      })
    ),
  });
}
